import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import certificatesService from '../services/certificatesService';
import DateInput from '../components/DateInput';
import styles from './CertificateEdit.module.css';

const CertificateEdit = () => {
  const { propertyId, documentId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [useMockData, setUseMockData] = useState(false);
  const [certificateData, setCertificateData] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    document_type_id: '',
    date_completed: '',
    date_expiration: '',
    date_contractor_appointment: '',
    visible_to_client: false
  });

  useEffect(() => {
    loadCertificateData();
  }, [propertyId, documentId]);

  const loadCertificateData = async () => {
    try {
      setLoading(true);
      const response = await certificatesService.getCertificateForEdit(propertyId, documentId);

      if (response.success) {
        const { document, property, propertyDocumentTypes, newExpirationDate } = response.data;
        setCertificateData({ document, property, propertyDocumentTypes, newExpirationDate });
        
        // Populate form data
        setFormData({
          name: document.name || document.type || '',
          document_type_id: document.type_id || '',
          date_completed: document.date_completed || '',
          date_expiration: document.date_expiration || newExpirationDate,
          date_contractor_appointment: document.date_contractor_appointment || '',
          visible_to_client: document.visible_to_client || false
        });
        setUseMockData(false);
      } else {
        throw new Error('Invalid response format');
      }
    } catch (error) {
      console.error('Error loading certificate data:', error);
      setUseMockData(true);
      // Use mock data as fallback
      const mockData = getMockCertificateData(propertyId, documentId);
      setCertificateData(mockData);
      setFormData(mockData.formData);
      toast.warning('Using mock data due to backend connection issue');
    } finally {
      setLoading(false);
    }
  };

  const getMockCertificateData = (propertyId, documentId) => {
    const mockDocument = {
      id: parseInt(documentId),
      name: 'Gas Safety Certificate',
      type: 'Gas Safety Certificate',
      type_id: 1,
      date_completed: '15/03/2024',
      date_expiration: '15/03/2025',
      date_contractor_appointment: '10/03/2024',
      visible_to_client: true
    };

    const mockProperty = {
      id: parseInt(propertyId),
      name: '123 Main Street, London',
      address: '123 Main Street, London, SW1A 1AA'
    };

    const mockPropertyDocumentTypes = [
      { id: 1, name: 'Gas Safety Certificate' },
      { id: 2, name: 'Electrical Safety Certificate' },
      { id: 3, name: 'Fire Safety Certificate' },
      { id: 4, name: 'Energy Performance Certificate' },
      { id: 5, name: 'Building Regulations Certificate' }
    ];

    return {
      document: mockDocument,
      property: mockProperty,
      propertyDocumentTypes: mockPropertyDocumentTypes,
      newExpirationDate: '15/03/2025',
      formData: {
        name: mockDocument.name,
        document_type_id: mockDocument.type_id,
        date_completed: mockDocument.date_completed,
        date_expiration: mockDocument.date_expiration,
        date_contractor_appointment: mockDocument.date_contractor_appointment,
        visible_to_client: mockDocument.visible_to_client
      }
    };
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setSubmitting(true);
      
      if (!useMockData) {
        const response = await certificatesService.updateCertificate(propertyId, documentId, formData);
        
        if (response.success) {
          toast.success(response.message || 'Certificate successfully updated');
          navigate('/clients/certificates');
        } else {
          toast.error(response.message || 'Failed to update certificate');
        }
      } else {
        // Mock update
        toast.success('Certificate successfully updated (Mock)');
        navigate('/clients/certificates');
      }
    } catch (error) {
      console.error('Error updating certificate:', error);
      toast.error('Failed to update certificate');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingSpinner}>
          <div className={styles.spinner}></div>
          <p>Loading certificate data...</p>
        </div>
      </div>
    );
  }

  if (!certificateData) {
    return (
      <div className={styles.container}>
        <div className={styles.errorMessage}>
          Certificate not found
        </div>
      </div>
    );
  }

  const { document, property, propertyDocumentTypes } = certificateData;

  return (
    <div className={styles.container}>
      {/* Breadcrumbs */}
      <nav className={styles.breadcrumb}>
        <ol>
          <li>
            <a href="/dashboard">Dashboard</a>
          </li>
          <li>
            <a href="/clients">Clients</a>
          </li>
          <li>
            <a href="/clients/certificates">Certificates</a>
          </li>
          <li className={styles.active}>Edit Certificate</li>
        </ol>
      </nav>

      {/* Content Area with Background */}
      <div className={styles.contentArea}>
        {/* Page Title */}
        <div className={styles.pageTitle}>
          <h1>Edit property document</h1>
        </div>

        {/* Form Panel */}
        <div className={styles.formPanel}>
          <div className={styles.panelBody}>
            <form onSubmit={handleSubmit} className={styles.form}>
              {/* Hidden property ID */}
              <input type="hidden" name="property_id" value={property.id} />

              {/* Document Name */}
              <div className={styles.formGroup}>
                <label htmlFor="name" className={styles.formLabel}>
                  Name <span className={styles.required}>*</span>
                </label>
                <div className={styles.inputWrapper}>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className={styles.formInput}
                    required
                  />
                </div>
              </div>

              {/* Document Type */}
              <div className={styles.formGroup}>
                <label htmlFor="document_type_id" className={styles.formLabel}>
                  Document Type <span className={styles.required}>*</span>
                </label>
                <div className={styles.inputWrapper}>
                  <div className={styles.readOnlyText}>{document.type}</div>
                  <input type="hidden" name="document_type_id" value={formData.document_type_id} />
                </div>
              </div>

              {/* Date Completed */}
              <div className={styles.formGroup}>
                <label htmlFor="date_completed" className={styles.formLabel}>
                  Date Completed
                </label>
                <div className={styles.inputWrapper}>
                  <DateInput
                    id="date_completed"
                    name="date_completed"
                    value={formData.date_completed}
                    onChange={handleInputChange}
                    className={styles.formInput}
                  />
                </div>
              </div>

              {/* Date Expiration */}
              <div className={styles.formGroup}>
                <label htmlFor="date_expiration" className={styles.formLabel}>
                  Date Expiration
                </label>
                <div className={styles.inputWrapper}>
                  <DateInput
                    id="date_expiration"
                    name="date_expiration"
                    value={formData.date_expiration}
                    onChange={handleInputChange}
                    className={styles.formInput}
                  />
                </div>
              </div>

              {/* Contractor Appointment Date */}
              <div className={styles.formGroup}>
                <label htmlFor="date_contractor_appointment" className={styles.formLabel}>
                  Contractor appointment date
                </label>
                <div className={styles.inputWrapper}>
                  <DateInput
                    id="date_contractor_appointment"
                    name="date_contractor_appointment"
                    value={formData.date_contractor_appointment}
                    onChange={handleInputChange}
                    className={styles.formInput}
                  />
                </div>
              </div>

              {/* Visible to Client */}
              <div className={styles.formGroup}>
                <label htmlFor="visible_to_client" className={styles.formLabel}>
                  Visible to client
                </label>
                <div className={styles.inputWrapper}>
                  <input
                    type="checkbox"
                    id="visible_to_client"
                    name="visible_to_client"
                    checked={formData.visible_to_client}
                    onChange={handleInputChange}
                    className={styles.checkbox}
                  />
                </div>
              </div>

              {/* Form Actions */}
              <div className={styles.formActions}>
                <button
                  type="submit"
                  className={`${styles.button} ${styles.saveButton}`}
                  disabled={submitting}
                >
                  {submitting ? 'Saving...' : 'Save document'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CertificateEdit; 